import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Remembers the last user-set open/closed state per accordion instance (keyed by
// details.id) in sessionStorage. A plain in-memory value is not enough here: this
// module can be re-evaluated from scratch when the facets AJAX update re-renders
// the whole section, which would otherwise discard any remembered state.
const OPEN_STATE_STORAGE_PREFIX = 'accordion-open-state:';

function readStoredOpenState(key) {
if (!key) return undefined;
try {
const value = sessionStorage.getItem(OPEN_STATE_STORAGE_PREFIX + key);
if (value === '1') return true;
if (value === '0') return false;
return undefined;
} catch (error) {
return undefined;
}
}

function writeStoredOpenState(key, isOpen) {
if (!key) return;
try {
sessionStorage.setItem(OPEN_STATE_STORAGE_PREFIX + key, isOpen ? '1' : '0');
} catch (error) {
// Ignore storage errors (e.g. private browsing quota).
}
}

class AccordionCustom extends HTMLElement {
get details() {
const details = this.querySelector('details');
if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');
return details;
}

get summary() {
const summary = this.details.querySelector('summary');
if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');
return summary;
}

get #disableOnMobile() {
return this.dataset.disableOnMobile === 'true';
}

get #disableOnDesktop() {
return this.dataset.disableOnDesktop === 'true';
}

get #closeWithEscape() {
return this.dataset.closeWithEscape === 'true';
}

get #stateKey() {
    const details = this.details;
    if (!details.id) return undefined;
    const formId = details.closest('form')?.id ?? '';
    return formId ? `${formId}::${details.id}` : details.id;
    }
    
#controller = new AbortController();

connectedCallback() {
this.#controller = new AbortController();
const { signal } = this.#controller;

this.#restoreOpenState();

this.addEventListener('keydown', this.#handleKeyDown, { signal });
this.summary.addEventListener('click', this.handleClick, { signal });
this.details.addEventListener('click', this.#handleContentClick, { signal });
this.details.addEventListener('toggle', this.#handleToggle, { signal });
mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
}

disconnectedCallback() {
// Disconnect all the event listeners
this.#controller.abort();
}

handleClick = (event) => {
const isMobile = isMobileBreakpoint();
const isDesktop = !isMobile;

// Stop default behaviour from the browser
if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
event.preventDefault();
return;
}
};

// Records this accordion's open/closed state whenever it changes, so a later
// connectedCallback can restore it even if this element gets destroyed and
// recreated by an unrelated section re-render.
#handleToggle = () => {
writeStoredOpenState(this.#stateKey, this.details.open);
};


#handleContentClick = (event) => {
if (this.details.open && event.target.closest('[data-accordion-close]')) {
this.summary.focus({ preventScroll: true });
this.details.open = false;
}
};

#handleMediaQueryChange = () => {
this.#setDefaultOpenState();
};

// Restores this accordion's last user-set open/closed state on (re)connect, if
// one was recorded. Falls back to the configured default state otherwise.
#restoreOpenState() {
const stored = readStoredOpenState(this.#stateKey);
if (stored === undefined) {
this.#setDefaultOpenState();
writeStoredOpenState(this.#stateKey, this.details.open);
} else {
this.details.open = stored;
}
}

#setDefaultOpenState() {
const isMobile = isMobileBreakpoint();
this.details.open =
(isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
(!isMobile && this.hasAttribute('open-by-default-on-desktop'));
}

#handleKeyDown(event) {
if (event.key === 'Escape' && this.#closeWithEscape) {
event.preventDefault();
this.summary.focus();
this.details.open = false;
}
}
}

customElements.get('accordion-custom') || customElements.define('accordion-custom', AccordionCustom);
